import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Link2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  ErrorState,
  Form,
  FullPageSpinner,
  cx,
  Badge,
  Field,
  Input,
  CONTROL_LG,
  KeyValue,
  Button,
  buttonClass,
  Breadcrumb,
  Section,
  useToast,
} from "@/components/ui";
import { portal } from "@/lib/api";
import { domainKeys, domainsApi } from "@/lib/domains";
import { siteHref } from "@/lib/format";
import { siteKeys } from "@/lib/queries";
import type { CustomDomain, Site } from "@/lib/types";

import { RecordIssue } from "./connect/RecordIssue";
import { ConnectError, siteDomainsPath, siteKindLabel, useSites } from "./connect/shared";
import { Heading, HeadTile, DomainPage, TwoColumns, Notice, RecordChip } from "@/components/domains/parts";

/** "https://www.Example.com/about" → "www.example.com" */
function normalizeHost(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");
}

const HOST = /^(?=.{3,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

interface Target {
  hostname: string;
  siteId: string;
}

/** Figma "Connect a domain you already own" (2081-4) and its record-mismatch state (2081-411). */
export function ConnectExternalDomainPage() {
  const sites = useSites();
  if (sites.isPending) return <FullPageSpinner />;
  if (sites.isError) return <ErrorState error={sites.error} onRetry={() => void sites.refetch()} />;
  return <ConnectExternal sites={sites.data} />;
}

function ConnectExternal({ sites }: { sites: Site[] }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const urlHost = normalizeHost(params.get("hostname") ?? "");
  const urlSite = sites.find((s) => s.id === params.get("site"));

  const [input, setInput] = useState(urlHost);
  const [pickedSite, setPickedSite] = useState<string>(urlSite?.id ?? sites[0]?.id ?? "");
  const [target, setTarget] = useState<Target | null>(null);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);

  // A hostname from the URL that's already on the site picks up where it was left.
  const resumeSiteId = urlSite?.id ?? pickedSite;
  const resume = useQuery({
    queryKey: siteKeys.domains(orgId, resumeSiteId),
    queryFn: () => portal.domains(resumeSiteId),
    enabled: !target && Boolean(urlHost && resumeSiteId),
  });
  const current: Target | null =
    target ??
    (urlHost && resume.data?.domains.some((d) => d.hostname === urlHost)
      ? { hostname: urlHost, siteId: resumeSiteId }
      : null);
  const site = sites.find((s) => s.id === (current?.siteId ?? pickedSite)) ?? null;

  const siteDomains = useQuery({
    queryKey: siteKeys.domains(orgId, current?.siteId ?? ""),
    queryFn: () => portal.domains(current!.siteId),
    enabled: Boolean(current),
    refetchInterval: (q) => {
      const d = q.state.data?.domains.find((x) => x.hostname === current?.hostname);
      return d && d.status !== "active" ? 10_000 : false;
    },
  });
  const runtime: CustomDomain | undefined = siteDomains.data?.domains.find(
    (d) => d.hostname === current?.hostname,
  );
  const active = runtime?.status === "active";

  const diagnose = useQuery({
    queryKey: [...domainKeys.all(orgId), "diagnose", current?.hostname ?? "", current?.siteId ?? ""],
    queryFn: () => domainsApi.diagnose(orgId, current!.hostname, current!.siteId),
    enabled: Boolean(current && runtime),
  });

  const refreshSite = (siteId: string) => {
    void qc.invalidateQueries({ queryKey: siteKeys.domains(orgId, siteId) });
    void qc.invalidateQueries({ queryKey: ["hosting", orgId] });
    void qc.invalidateQueries({ queryKey: ["overview", orgId] });
  };

  // Step 1: put the hostname on the site, so the runtime issues its TXT verification record.
  const start = useMutation({
    mutationFn: async (next: Target) => {
      // The domain and its www. address both serve the site (they count as one custom domain).
      const onSite = new Set((await portal.domains(next.siteId)).domains.map((d) => d.hostname));
      if (!onSite.has(next.hostname)) await portal.addDomain(next.siteId, next.hostname);
      const www = `www.${next.hostname}`;
      if (!next.hostname.startsWith("www.") && !onSite.has(www)) await portal.addDomain(next.siteId, www);
      return next;
    },
    onSuccess: (next) => {
      setTarget(next);
      setCheckedAt(null);
      setShowAll(false);
      refreshSite(next.siteId);
      setParams({ hostname: next.hostname, site: next.siteId }, { replace: true });
    },
  });

  // "I've added these records" / "Recheck now": ask the runtime to verify, then look at the DNS again.
  const check = useMutation({
    mutationFn: async () => {
      if (!current) return;
      const d = runtime ?? (await portal.addDomain(current.siteId, current.hostname));
      if (d.status !== "active") await portal.verifyDomain(d.id);
      const www = (await portal.domains(current.siteId)).domains.find(
        (x) => x.hostname === `www.${current.hostname}`,
      );
      if (www && www.status !== "active") await portal.verifyDomain(www.id).catch(() => undefined);
    },
    onSettled: async () => {
      setCheckedAt(Date.now());
      setShowAll(false);
      if (current) refreshSite(current.siteId);
      await Promise.all([siteDomains.refetch(), diagnose.refetch()]);
    },
  });

  const reset = () => {
    setTarget(null);
    setCheckedAt(null);
    setShowAll(false);
    setInput("");
    setParams({}, { replace: true });
  };
  const toast = useToast();
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await portal.removeDomain(id);
      const www = current
        ? (await portal.domains(current.siteId)).domains.find((x) => x.hostname === `www.${current.hostname}`)
        : undefined;
      if (www) await portal.removeDomain(www.id);
    },
    onSuccess: () => {
      if (current) refreshSite(current.siteId);
      reset();
      toast.show({ tone: "success", title: "Domain removed from the site" });
    },
  });

  const host = normalizeHost(input);
  const valid = HOST.test(host);
  const dirty = !current || host !== current.hostname || pickedSite !== current.siteId;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid && pickedSite) start.mutate({ hostname: host, siteId: pickedSite });
  };

  const records = diagnose.data?.records ?? [];
  const registrar = diagnose.data?.registrar ?? null;
  const checking = check.isPending || (diagnose.isFetching && checkedAt !== null);
  const mismatched = records.some((r) => r.matches === false);

  if (current && !active && checkedAt && mismatched && !showAll && !checking)
    return (
      <RecordIssue
        hostname={current.hostname}
        records={records}
        checkedAt={checkedAt}
        checking={checking}
        error={check.error}
        onRecheck={() => check.mutate()}
        onShowAll={() => setShowAll(true)}
      />
    );

  const copyAll = () => {
    const text = records.map((r) => `${r.type}\t${r.host}\t${r.value}`).join("\n");
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "...", to: "/domains" },
          { label: "connect a domain you own" },
        ]}
      />
      <Heading
        size={24}
        tile={
          <HeadTile tone={active ? "success" : "pending"}>
            {active ? (
              <CheckCircle2 className="size-5" aria-hidden />
            ) : (
              <Link2 className="size-5 text-[#e28e00]" aria-hidden />
            )}
          </HeadTile>
        }
        title={active && current ? `${current.hostname} is connected` : "Connect a domain you already own"}
        subtitle={
          active
            ? `It now opens ${site ? `${site.name}` : "your website"} — it stays registered where it is`
            : "This connects your domain — it doesn't transfer it into CortexGrip"
        }
      />

      {sites.length === 0 ? (
        <Section title="You don't have a website yet">
          <p className="text-sm text-fg-subtle">
            Build your website, or upload one on Hosting, then come back to connect your domain.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <Link to="/websites" className={buttonClass("brand", "lg")}>
              Go to Website
            </Link>
            <Link to="/hosting" className={buttonClass("secondary", "lg")}>
              Go to Hosting
            </Link>
          </div>
        </Section>
      ) : active && current && site ? (
        <Connected hostname={current.hostname} site={site} onAnother={reset} />
      ) : (
        <TwoColumns
          main={
            <>
              <Section title="1. Enter your domain">
                <Form
                  onSubmit={submit}
                  error={start.error}
                  validate={() => ({ hostname: !valid && "Enter a domain name like example.com" })}
                  className="flex flex-col gap-3"
                >
                  <div className="flex flex-col gap-3 md:flex-row">
                    <Field
                      label="Domain"
                      name="hostname"
                      required
                      error={input && !valid ? "Enter a domain name like example.com" : undefined}
                      hint={
                        current && !dirty && diagnose.data
                          ? registrar
                            ? `We've detected this is registered with ${registrar}.`
                            : "We couldn't tell who it's registered with — add the records wherever its DNS is managed."
                          : undefined
                      }
                    >
                      {(control) => (
                        <Input
                          size="lg"
                          {...control}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          placeholder="yourdomain.com"
                          autoComplete="off"
                          spellCheck={false}
                          invalid={Boolean(input) && !valid}
                        />
                      )}
                    </Field>
                    {sites.length > 1 && (
                      <Field size="lg" label="Website" name="site_id" required>
                        {(control) => (
                          <select
                            {...control}
                            value={pickedSite}
                            onChange={(e) => setPickedSite(e.target.value)}
                            className={CONTROL_LG}
                          >
                            {sites.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name} — {siteKindLabel(s)} ({s.hostname})
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                    )}
                  </div>
                  {dirty && (
                    <div>
                      <Button
                        variant="brand"
                        size="lg"
                        type="submit"
                        disabledReason={pickedSite ? undefined : "Choose a website to connect to"}
                        loading={start.isPending}
                      >
                        Continue
                      </Button>
                    </div>
                  )}
                  {start.error && <ConnectError error={start.error} />}
                </Form>
              </Section>

              <Section title="2. Choose how to connect">
                <div className="grid gap-3 md:grid-cols-2">
                  <div
                    aria-disabled
                    className="flex flex-col gap-1.5 rounded-xl border border-border bg-[#fdfbf9] p-3.5 opacity-70"
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-fg">Automatic</p>
                      <Badge tone="neutral" size="sm">
                        Coming soon
                      </Badge>
                    </div>
                    <p className="text-xs text-fg-subtle">
                      Sign in to your registrar and we'll update the records for you.
                    </p>
                  </div>
                  <div
                    aria-current="true"
                    className="flex flex-col gap-1.5 rounded-xl border-[1.5px] border-brand-500 bg-[#fef8f6] p-3.5"
                  >
                    <p className="text-sm font-semibold text-fg">Manual DNS setup</p>
                    <p className="text-xs text-fg-subtle">We'll give you records to add yourself, anytime.</p>
                  </div>
                </div>
              </Section>

              <Section
                title="3. Add these records at your registrar"
                subtitle={
                  current
                    ? `Add exactly these records in your ${registrar ?? "domain's"} DNS settings, then come back here to check.`
                    : "Enter your domain above and we'll list the records to add."
                }
              >
                {current && diagnose.isPending && (
                  <p className="text-sm text-fg-subtle">Looking up the records…</p>
                )}
                <ErrorAlert error={diagnose.error ?? siteDomains.error} />
                {records.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {records.map((r) => (
                      <RecordChip
                        key={`${r.type}-${r.host}-${r.value}`}
                        type={r.type}
                        host={r.host}
                        value={r.value}
                      />
                    ))}
                  </div>
                )}
                {current && !runtime && siteDomains.isSuccess && (
                  <p className="text-xs text-fg-subtle">
                    The verification record appears once {current.hostname} is added to{" "}
                    {site?.name ?? "your website"}.
                  </p>
                )}
                <ErrorAlert error={check.error} />
                <div className="flex flex-wrap gap-2.5">
                  <Button
                    variant="brand"
                    size="lg"
                    onClick={() => check.mutate()}
                    disabledReason={
                      !current
                        ? "Enter your domain first"
                        : dirty
                          ? "Press Continue to save your changes first"
                          : undefined
                    }
                    loading={check.isPending}
                  >
                    I've added these records
                  </Button>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={copyAll}
                    disabledReason={records.length === 0 ? "There are no records to copy yet" : undefined}
                  >
                    {copied ? "Copied" : "Copy all records"}
                  </Button>
                </div>
              </Section>
            </>
          }
          side={
            <>
              <Section title="Connection status">
                <KeyValue variant="detail" label="Domain entered">
                  {current ? (
                    <Badge size="md" tone="success">
                      {current.hostname}
                    </Badge>
                  ) : (
                    <Badge size="md" tone="neutral">
                      Not yet
                    </Badge>
                  )}
                </KeyValue>
                <KeyValue variant="detail" label="Method">
                  <Badge size="md" tone="neutral">
                    Manual DNS
                  </Badge>
                </KeyValue>
                <KeyValue variant="detail" label="Records verified">
                  {!current ? (
                    <Badge size="md" tone="neutral">
                      Not yet
                    </Badge>
                  ) : checking || (checkedAt === null && diagnose.isFetching) ? (
                    <Badge size="md" tone="warning">
                      Checking…
                    </Badge>
                  ) : records.length > 0 && records.every((r) => r.matches === true) ? (
                    <Badge size="md" tone="success">
                      Verified
                    </Badge>
                  ) : runtime?.status === "failed" ? (
                    <Badge size="md" tone="danger">
                      Not yet
                    </Badge>
                  ) : (
                    <Badge size="md" tone="warning">
                      Not yet
                    </Badge>
                  )}
                </KeyValue>
                {checkedAt && runtime?.check_error && (
                  <p className="text-xs text-fg-subtle">{runtime.check_error}</p>
                )}
                {current && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="lg"
                      variant="secondary"
                      onClick={() => check.mutate()}
                      loading={check.isPending}
                      disabledReason={dirty ? "Press Continue to save your changes first" : undefined}
                    >
                      Recheck now
                    </Button>
                    {runtime && (
                      <Button
                        size="lg"
                        variant="ghost"
                        onClick={() => remove.mutate(runtime.id)}
                        loading={remove.isPending}
                      >
                        Remove from {site?.name ?? "website"}
                      </Button>
                    )}
                  </div>
                )}
                <ErrorAlert error={remove.error} />
              </Section>
              <Notice tone="cream" title="DNS changes can take time">
                <span className="text-xs">
                  Propagation is often instant but can take up to 24-48 hours depending on your registrar.
                </span>
              </Notice>
              <div className="flex flex-col gap-2 rounded-[14px] border border-border p-4">
                <p className="text-[15px] font-semibold text-fg">Or transfer it to CortexGrip instead</p>
                <p className="text-xs text-fg-subtle">
                  Move the registration here and we'll manage its DNS for you — no records to copy.
                </p>
                <Link
                  to={`/domains/find?transfer=1${current ? `&q=${encodeURIComponent(current.hostname)}` : ""}`}
                  className={cx(buttonClass("secondary", "lg"), "self-start")}
                >
                  Transfer a domain
                </Link>
              </div>
            </>
          }
        />
      )}
    </DomainPage>
  );
}

function Connected({ hostname, site, onAnother }: { hostname: string; site: Site; onAnother: () => void }) {
  return (
    <Notice
      tone="success"
      title={`${hostname} is live`}
      actions={
        <>
          <a
            href={siteHref(hostname, site.link_scheme)}
            target="_blank"
            rel="noreferrer"
            className={buttonClass("brand", "lg")}
          >
            View website
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
          <Link to={siteDomainsPath(site)} className={buttonClass("secondary", "lg")}>
            Manage {site.name}'s domains
          </Link>
          <Button size="lg" variant="ghost" onClick={onAnother}>
            Connect another domain
          </Button>
        </>
      }
    >
      Visitors going to {hostname} now see {site.name}. Your CortexGrip address keeps working too, and the
      certificate is issued automatically.
    </Notice>
  );
}
